
// FormType used throughout the application
export const FormType = {
	Assay: 'Assay',
	BoxAndPhotos: 'BoxAndPhotos',
	Collar: 'Collar',
	Density: 'Density',
	Despatch: 'Despatch',
	DrillReport: 'DrillReport',
	Geotech: 'Geotech',
	Lithology: 'Lithology',
	MagneticSuscepbility: 'MagneticSuscepbility',
	Mineralization: 'Mineralization',
	QtzVein: 'QtzVein',
	Samples: 'Samples',
	Structure: 'Structure',
	Survey: 'Survey',
	DrillholeProgram: 'DrillholeProgram',
	TargetProgram: 'TargetProgram'
};

export enum ActionStatus {
	Pending = 'PENDING',
	InProgress = 'IN_PROGRESS',
	Done = 'DONE'
}

export type InitiativeAction = BaseEntity & {
	id: string;
	title: string;
	description?: string;
	deadline: string;
	responsible: string;
	completedAt?: string;
	status: ActionStatus;
	initiativeId: string;
	files?: any[]; // Using generic type for now as PmoFile type might not be strictly defined here yet
};

export enum EntityName {
	COLLAR = 'collar',
	STRUCTURE = 'structure',
	LITHOLOGY = 'lithology',
	QTZ_VEIN = 'qtz-vein',
	GEOTECH = 'geotech',
	DENSITY = 'density',
	SURVEY = 'survey',
	DRILL_REPORT = 'drill-report',
	TARGET = 'subsite',
	DRILLHOLE_PROGRAM = 'dhp',
}

export type QAQCSample = BaseEntity & {
	code: string;
	principleCertifiedValues1: number;
	principleCertifiedValues2?: number;
	principleCertifiedValues3?: number;
	matrix?: string;
	unit: string;
	element?: string;
	mineralisation?: string;
	sampleType: 'standard' | 'blank';
	weight?: number;
	sdCertified?: number;
	stock: number;
	type?: string;
	comments?: string;
	certifiedValues?: QAQCCertifiedValue[];
};

export type Element = BaseEntity & {
	siteId: string;
	name: string;
	abbreviation: string;
	digits: number;
	defaultUnit: string;
	priority: number;
	highgrade?: number;
	hexColor?: string;
	lowgrade?: number;
	mediumgrade?: number;
	alias?: string;
	type?: string;
	status?: string;
}

export type QAQCCertifiedValue = BaseEntity & {
	value: number;
	stdDeviation?: number;
	unit: string;
	warningLimitSigma?: number;
	controlLimitSigma?: number;
	methodElementId: string;
	qaqcSampleId: string;
	methodElement?: MethodElement;
}

export type Method = BaseEntity & {
	siteId: string;
	name: string;
	abbreviation: string;
	description?: string;
	priority: number;
	alias?: string;
	status?: string;
	methodElements?: MethodElement[];
}

export type MethodElement = BaseEntity & {
	methodId: string;
	elementId: string;
	detectionLimitMin?: number;
	detectionLimitMax?: number;
	belowLimitValue?: number;
	aboveLimitValue?: number;
	applyAvgForGroupAlias?: boolean;
	showInStripLog?: boolean;
	status?: string;
	method?: Method;
	element?: Element;
};

// BaseEntity type for all entities in the application
export type BaseEntity = {
	id?: string;
	CreateAt?: Date;
	UpdateAt?: Date;
	createdBy?: string;
	updatedBy?: string;
};

/** KeycloakUser represents a user from Keycloak identity provider */
export type KeycloakUser = {
  id: string;
  createdTimestamp: number;
  username: string;
  enabled: boolean;
  totp: boolean;
  emailVerified: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  disableableCredentialTypes: string[];
  requiredActions: string[];
  notBefore: number;
  access?: {
    manageGroupMembership: boolean;
    view: boolean;
    mapRoles: boolean;
    impersonate: boolean;
    manage: boolean;
  };

  /**
   * @description NÃO UTILIZAR - esse parâmetro é injetado temporáriamente em uma única tela.
   */
  groups?: any[];
  /**
   * @description NÃO UTILIZAR - esse parâmetro é injetado temporáriamente em uma única tela.
   */
  roles?: any[];
};

export enum Endpoint {
	ACCOUNT = 'account',
	COST_CENTER = 'costcenter',
	COORDINATE_SYSTEM = 'epsg',
	DRILLHOLE_PROGRAM = 'drillholeprogram',
	COLLAR_INSTRUMENT = 'collar-instrument',
	COMPANY = 'company',
	COLLAR = 'collar',
	METHODS = 'methods',
	METHOD_ELEMENTS = 'method-elements',
	ELEMENTS = 'elements',
	QAQC_SAMPLES = 'qaqcsamples',
	QAQC_CERTIFIED_VALUES = 'qaqc-certified-values',
	MINERAL = 'mineral',
	EMAIL_RECIPIENT = 'email-recipient',
	USER = 'user',
	USERS_BY_GROUP = 'user/group',
  USERS_ALL = 'user/all',
	TARGET = 'subsite',
	AREA = 'area',
	CAMPAIGN = 'campaign',
	UNIT = 'unit',
	INITIATIVES = 'pmo/initiatives',
	EVENT = 'event',
	PMO_FILES = 'pmo/files',
	INITIATIVE_QUESTIONS = 'pmo/questions',
}
// Documents tree
export interface TreeNode {
	id?: string;
	name: string;
	icon?: string;
	children?: TreeNode[];
	level?: number;
	expandable?: boolean;
}

export enum CollarStatus {
	IN_PROGRESS = 'in progress',
	PROGRAMMED = 'programmed',
	CANCELED = 'canceled',
	FINISHED = 'Finished',
	ABORTED = 'aborted',
	DONE = 'done',
}

// wip
export type Area = BaseEntity & {
	name: string;
	unitName?: string;
	unitId?: string;
	responsibleEmail?: string;
}

export type Initiative = BaseEntity & {
	companyId?: string;
	name: string;
	description: string;
	mainCategory: string;
	effortLevel: string;
	investmentValueType: string;
	investmentValue?: number;
	implementationPeriod?: string;
	startDate?: Date;
	endDate?: Date;
	mainResponsible?: string;
	responsibleEmail?: string;
	responsibleContact?: string;
	observations?: string;
	expectedResults: string;
	expectedBenefits?: string;
	status?: string;
	classification: string[];
	responsibleRole: string | 'idealizer' | 'participant';
	area?: Area;
	areaId?: string;
	campaignId?: string;
	campaignName?: string;
	relatedAreas?: string[];
	impactedAreas?: Area[];
	availability?: any;
	/** A ideia ja estava implementada na empresa quando foi cadastrada (opcao "Iniciativa ja implementada"). Diferente de status COMPLETED, que e o fim do processo de aprovacao. */
	alreadyImplemented?: boolean;

	// Validation Form Fields (validar-iniciativa)
	strategicRelevance?: string;
	technicalViability?: string;
	businessImpact?: string;
	requiredResources?: string;

	// New assessment fields (avaliar-iniciativa)
	impact?: number;
	simplicity?: number;
	scalability?: number;
	innovationDegree?: number;
	maintainability?: number;
	score?: number; // Calculated weighted score: (impact x 0.35) + (simplicity x 0.30) + (scalability x 0.20) + (innovationDegree x 0.10) + (maintainability x 0.05)

	comments?: any[];

	createdAt?: Date;
	updatedAt?: Date;

	members?: any[];
	questions?: InitiativeQuestion[];
	skipProcess?: boolean;
	currentTaskName?: string;
	pendingPerson?: string;
};

export enum InitiativeStatus {
	DRAFT = 'DRAFT',
	PENDING = 'PENDING',
	UNDER_REVIEW = 'UNDER_REVIEW',
	APPROVED = 'APPROVED',
	REJECTED = 'REJECTED',
	IN_EXECUTION = 'IN_EXECUTION',
	IMPLANTED = 'IMPLANTED',
	COMPLETED = 'COMPLETED',
	CANCELLED = 'CANCELLED',
	PAUSED = 'PAUSED'
}

export type TimelineEvent = BaseEntity & {
	description?: string;
	date?: Date;
	userId?: string;
	userName?: string;
	userEmail?: string;
	link?: string;
	entity?: string;
	entityId?: string;
	processId?: string;
	action?: string;
	taskId?: string;
};

export type PmoFile = BaseEntity & {
	fileName: string;
	originalName: string;
	mimeType: string;
	size: number;
	path?: string;
	initiativeId: string;
	uploadedBy?: string;
	uploadedAt?: Date;
};

export type InitiativeQuestion = BaseEntity & {
	question: string;
	answer?: string;
	authorName?: string;
	authorEmail?: string;
	responsibleName?: string;
	responsibleEmail?: string;
	initiativeId: string;
}

/** Dashboard statistics for innovation initiatives */
export type InnovationDashboardStats = {
	totalInitiatives: number;
	prioritizedInitiatives: number;
	implementedInitiatives: number;
	averageScore: number;
	engagementRate: number;
	monthlyEvolution?: MonthlyDataPoint[];
	areaDistribution?: AreaDataPoint[];
	categoryDistribution?: CategoryDataPoint[];
	costPerUnit?: CostDataPoint[];
	avgEvaluationTime?: EvaluationTimeDataPoint[];
	evaluationCriteria?: EvaluationCriteriaDataPoint[];
	prioritizedComparison?: PrioritizedComparisonDataPoint[];
}

/** Monthly data point for charts */
export type MonthlyDataPoint = {
	name: string;
	value: number;
}

/** Area distribution data point */
export type AreaDataPoint = {
	name: string;
	value: number;
	color: string;
}

/** Category distribution data point */
export type CategoryDataPoint = {
	name: string;
	value: number;
	percentage?: number;
}

/** Cost per unit data point */
export type CostDataPoint = {
	name: string;
	value: number;
	color?: string;
}

/** Evaluation time data point */
export type EvaluationTimeDataPoint = {
	period: string;
	time: number;
}

/** Evaluation criteria data point */
export type EvaluationCriteriaDataPoint = {
	criteria: string;
	value: number;
}

/** Prioritized comparison data point */
export type PrioritizedComparisonDataPoint = {
	name: string;
	prioritized: number;
	notPrioritized: number;
}

export type CompanyType = BaseEntity & {
	name?: string;
	cnpj?: string;
	description?: string;
	emailService?: string;
	logo?: string;
	internal?: boolean;
	drillHoleOperate?: boolean;
	drillHoleSurvey?: boolean;
	drilling?: boolean;
	laboratory?: boolean;
	isDelete?: boolean;
}

export type SimpleCompanyType = BaseEntity & {
	name: string;
	cnpj?: string;
	logo?: string;
	isDelete?: boolean;
}

export type AreaType = BaseEntity & {
	name: string;
	unitId?: string;
	unit?: { id: string; name: string };
	responsibleEmail?: string;
	isDelete?: boolean;
}

export type CampaignType = BaseEntity & {
	name: string;
	start?: Date;
	end?: Date;
	unitIds?: string[];
	units?: UnitType[];
	description?: string;
	isDelete?: boolean;
}

export type UnitType = BaseEntity & {
	name: string;
	code?: string;
	locale?: string;
	isDelete?: boolean;
}

export type ManagementType = BaseEntity & {
	name: string;
	areaIds?: string[];
	areas?: { id: string; name: string }[];
	isDelete?: boolean;
}

export type UserItem = {
	id?: string;
	createdTimestamp?: number;
	username?: string;
	enabled?: boolean;
	totp?: boolean;
	emailVerified?: boolean;
	firstName?: string;
	lastName?: string;
	email?: string;
	disableableCredentialTypes?: unknown[];
	requiredActions?: unknown[];
	notBefore?: number;
}
